/**
 * RmsAuthRefreshInterceptor — refresh silencioso de JWT + tratamento de error codes.
 *
 * Responsabilidades:
 *   1. 401 TOKEN_EXPIRED → tenta refresh silencioso (POST /auth/refresh)
 *   2. Fila de requests simultâneos: enfileira enquanto refresh está em andamento
 *   3. Fallback Firebase: se refresh falha, tenta ensureRmsJwt() (re-exchange via Firebase)
 *   4. Se ambos falham → clearTokens() + toast + navega para /login
 *   5. Outros 401 (TOKEN_REVOKED, USER_BLOCKED, etc.) → logout imediato com mensagem específica
 *   6. 403 com código conhecido → logout com mensagem (USER_INACTIVE, USER_BLOCKED, etc.)
 *
 * Ordem no CoreModule: registrado APÓS RmsAuthTokenInterceptor.
 * Na cadeia de resposta, interceptors são processados em ordem inversa — este roda primeiro.
 */

import {
    HttpClient,
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environment';
import { NavController, ToastController } from '@ionic/angular';
import { BehaviorSubject, from, Observable, of, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { keysLocalStorage } from 'src/constant/keys_localstorage';
import { RmsAuthService } from '../services/rms-auth.service';

/** URLs de autenticação que NUNCA devem ser interceptadas (evita loops) */
const AUTH_URLS = [
    '/auth/refresh',
    '/auth/login',
    '/auth/firebase/exchange',
    '/auth/firebase/custom-token',
];

const PLUTUS_API_MATCHES = [
    'localhost:4000',
    'api-homol-plutus.rmscorretora.com.br',
    'api-hplutus.rmscorretora.com.br',
    'api-plutus.rmscorretora.com.br',
];

/** Mensagens de erro por código de erro da API */
const ERROR_MESSAGES: Record<string, string> = {
    USER_INACTIVE: 'Sua conta foi desativada. Entre em contato com o suporte.',
    USER_BLOCKED: 'Conta bloqueada. Entre em contato com o suporte.',
    ACCOUNT_NOT_FOUND: 'Conta não encontrada.',
    TENANT_NOT_FOUND: 'Você ainda não está vinculado a uma empresa.',
    TOKEN_REVOKED: 'Sua sessão foi encerrada. Faça login novamente.',
    SESSION_EXPIRED: 'Sessão expirada. Faça login novamente.',
    SESSION_SUPERSEDED: 'Sua conta foi acessada em outro dispositivo. Faça login novamente.',
};

interface RefreshResponse {
    success: boolean;
    data: {
        token: string;
        refreshToken: string;
    };
}

@Injectable()
export class RmsAuthRefreshInterceptor implements HttpInterceptor {
    /** null = refresh em andamento | string = novo token disponível */
    private refreshTokenSubject = new BehaviorSubject<string | null>(null);
    private isRefreshing = false;

    constructor(
        private rmsAuthService: RmsAuthService,
        private navCtrl: NavController,
        private toastCtrl: ToastController,
        private http: HttpClient,
    ) {}

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Nunca intercepta URLs de autenticação
        if (this.isAuthUrl(req.url)) {
            return next.handle(req);
        }

        // Requisições protegidas pelo ecossistema RMS (Auth Service, Plutus ou requisições autenticadas)
        const isRmsProtected = this.isProtectedRmsRequest(req);

        return next.handle(req).pipe(
            catchError((error: HttpErrorResponse) => {
                const errorCode = this.extractErrorCode(error);

                // Single active session (anti-fraude): SESSION_SUPERSEDED pode vir de
                // QUALQUER backend (Plutus, Auth Service) — login em outro dispositivo
                // derrubou esta sessão. Logout imediato independente da origem.
                if (error.status === 401 && errorCode === 'SESSION_SUPERSEDED') {
                    this.forceLogout(ERROR_MESSAGES['SESSION_SUPERSEDED']);
                    return throwError(() => error);
                }

                if (isRmsProtected) {
                    if (error.status === 401) {
                        return this.handle401(req, next, error);
                    }

                    if (error.status === 403) {
                        return this.handle403(error);
                    }
                }

                return throwError(() => error);
            }),
        );
    }

    // ─── Handlers ────────────────────────────────────────────────────────────────

    private handle401(
        req: HttpRequest<any>,
        next: HttpHandler,
        error: HttpErrorResponse,
    ): Observable<HttpEvent<any>> {
        // Se a requisição já foi retentada uma vez e ainda deu 401, evita loop infinito
        if (req.headers.has('X-Rms-Retry')) {
            return throwError(() => error);
        }

        const errorCode = this.extractErrorCode(error);

        // Códigos terminais: sessão revogada, usuário bloqueado, conta inexistente
        if (this.isTerminalAuthError(errorCode)) {
            const message = ERROR_MESSAGES[errorCode] ?? 'Sessão inválida. Faça login novamente.';
            this.forceLogout(message);
            return throwError(() => error);
        }

        // Qualquer outro 401 não-terminal (TOKEN_EXPIRED, AUTHENTICATION_ERROR, not_authenticated,
        // token expirado ou ausente durante boot): tenta refresh silencioso e retry da requisição
        return this.handleTokenExpired(req, next);
    }

    private handle403(error: HttpErrorResponse): Observable<never> {
        const errorCode = this.extractErrorCode(error);
        const message = ERROR_MESSAGES[errorCode];

        if (message) {
            this.forceLogout(message);
        } else {
            this.showToast('Acesso negado. Você não tem permissão para esta ação.');
        }

        return throwError(() => error);
    }

    private handleTokenExpired(
        req: HttpRequest<any>,
        next: HttpHandler,
    ): Observable<HttpEvent<any>> {
        if (this.isRefreshing) {
            // Refresh em andamento — enfileira este request
            return this.refreshTokenSubject.pipe(
                filter((token) => token !== null),
                take(1),
                switchMap((token) => next.handle(this.cloneWithToken(req, token as string))),
            );
        }

        // Inicia o processo de refresh
        this.isRefreshing = true;
        this.refreshTokenSubject.next(null);

        return this.doRefresh().pipe(
            switchMap((newToken: string) => {
                this.isRefreshing = false;
                this.refreshTokenSubject.next(newToken);
                return next.handle(this.cloneWithToken(req, newToken));
            }),
            catchError((refreshError) => {
                this.isRefreshing = false;
                // Propaga o erro para desbloquear requests enfileirados (filter(token !== null) nunca emitiria)
                this.refreshTokenSubject.error(refreshError);
                // Reinicializa o subject para o próximo ciclo de refresh
                this.refreshTokenSubject = new BehaviorSubject<string | null>(null);
                this.forceLogout('Sessão expirada. Faça login novamente.');
                return throwError(() => refreshError);
            }),
        );
    }

    // ─── Refresh Logic ───────────────────────────────────────────────────────────

    /**
     * Tenta renovar o token via:
     *   1. POST /auth/refresh (refresh token)
     *   2. Fallback: ensureRmsJwt() via Firebase re-exchange
     *
     * Retorna Observable<string> com o novo token, ou lança erro se ambos falham.
     */
    private doRefresh(): Observable<string> {
        const refreshToken = this.rmsAuthService.getStoredRefreshToken();

        if (refreshToken && environment.rmsAuthServiceUrl) {
            return this.http
                .post<RefreshResponse>(`${environment.rmsAuthServiceUrl}/auth/refresh`, {
                    refreshToken,
                })
                .pipe(
                    switchMap((response) => {
                        if (response?.success && response?.data?.token) {
                            localStorage.setItem(keysLocalStorage.rmsJwtToken, response.data.token);
                            localStorage.setItem(
                                keysLocalStorage.rmsRefreshToken,
                                response.data.refreshToken,
                            );
                            return of(response.data.token);
                        }
                        throw new Error('Resposta de refresh inválida');
                    }),
                    catchError((err) => {
                        return this.doFirebaseFallback();
                    }),
                );
        }

        // Sem refresh token → vai direto para o fallback Firebase
        return this.doFirebaseFallback();
    }

    /**
     * Fallback: usa ensureRmsJwt() para re-exchange via Firebase.
     * Retorna Observable<string> com o novo token ou lança erro.
     */
    private doFirebaseFallback(): Observable<string> {
        return from(this.rmsAuthService.ensureRmsJwt()).pipe(
            switchMap(() => {
                const newToken = this.rmsAuthService.getStoredToken();
                if (newToken) {
                    return of(newToken);
                }
                return throwError(() => new Error('Firebase fallback não obteve token'));
            }),
        );
    }

    // ─── Helpers ─────────────────────────────────────────────────────────────────

    private cloneWithToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
        return req.clone({
            setHeaders: {
                Authorization: `Bearer ${token}`,
                'X-Rms-Retry': '1',
            },
        });
    }

    private isProtectedRmsRequest(req: HttpRequest<any>): boolean {
        const isAuth = Boolean(
            environment.rmsAuthServiceUrl && req.url.includes(environment.rmsAuthServiceUrl),
        );
        const isPlutus = PLUTUS_API_MATCHES.some((match) => req.url.includes(match));
        const hasAuthHeader = req.headers.has('Authorization');
        return isAuth || isPlutus || hasAuthHeader;
    }

    private isTerminalAuthError(errorCode: string): boolean {
        return (
            errorCode === 'TOKEN_REVOKED' ||
            errorCode === 'USER_INACTIVE' ||
            errorCode === 'USER_BLOCKED' ||
            errorCode === 'ACCOUNT_NOT_FOUND' ||
            errorCode === 'SESSION_SUPERSEDED' ||
            errorCode === 'TENANT_NOT_FOUND'
        );
    }

    private extractErrorCode(error: HttpErrorResponse): string {
        const err = error?.error;
        return (
            err?.error?.details?.code ??
            err?.error?.code ??
            err?.code ??
            err?.error?.error_key ??
            err?.error_key ??
            ''
        );
    }

    private isAuthUrl(url: string): boolean {
        return AUTH_URLS.some((authUrl) => url.includes(authUrl));
    }

    private forceLogout(message: string): void {
        this.rmsAuthService.clearTokens();
        this.showToast(message);
        this.navCtrl.navigateRoot('/login');
    }

    private showToast(message: string): void {
        this.toastCtrl
            .create({
                message,
                duration: 4000,
                position: 'top',
                color: 'danger',
                buttons: [{ text: 'Ok', role: 'cancel' }],
            })
            .then((toast) => toast.present())
            .catch((err) => console.warn('[RmsAuthRefreshInterceptor] Toast falhou:', err));
    }
}
